import type { APIMethod } from '@hooks/usePermissions';
import { useAPIPermission } from '@hooks/usePermissions';
import type { ReactNode } from 'react';
import React from 'react';

interface PermissionGuardProps {
  /**
   * Single API method or array of API methods to check permissions for
   */
  apiMethod: APIMethod | APIMethod[];

  /**
   * Children to render if user has permission
   */
  children: ReactNode;

  /**
   * Optional fallback to render when permission is denied
   * If not provided, nothing is rendered
   */
  fallback?: ReactNode;

  /**
   * Permission logic when multiple methods are provided: 'any' (OR) or 'all' (AND)
   * - 'any': User needs at least one of the specified permissions
   * - 'all': User needs all of the specified permissions
   * @default 'all'
   */
  requirementType?: 'any' | 'all';

  /**
   * If true, show the fallback with an explanation tooltip
   * @default false
   */
  showFallbackReason?: boolean;
}

/**
 * PermissionGuard component that conditionally renders children based on API permissions.
 *
 * Hides entire sections of UI when user lacks permissions, providing better UX than
 * showing disabled elements. Supports both single and multiple permission checks.
 *
 * @example Single permission check
 * ```tsx
 * import { PermissionGuard } from '@components/PermissionGuard';
 * import { API_METHODS } from '@hooks/usePermissions';
 *
 * function CaseActions() {
 *   return (
 *     <div>
 *       {/* Only show delete button if user has permission *\/}
 *       <PermissionGuard apiMethod={API_METHODS.CaseRecordsService.DeleteCaseRecord}>
 *         <Button variant="destructive">Delete Case</Button>
 *       </PermissionGuard>
 *
 *       {/* Show fallback message when permission denied *\/}
 *       <PermissionGuard
 *         apiMethod={API_METHODS.CaseRecordsService.UpdateCaseRecord}
 *         fallback={<Text muted>You cannot edit this case</Text>}
 *       >
 *         <Button>Edit Case</Button>
 *       </PermissionGuard>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Multiple permissions with OR logic
 * ```tsx
 * // Show edit section if user can update OR delete
 * <PermissionGuard
 *   apiMethod={[
 *     API_METHODS.CaseRecordsService.UpdateCaseRecord,
 *     API_METHODS.CaseRecordsService.DeleteCaseRecord,
 *   ]}
 *   requirementType="any"
 * >
 *   <CaseEditForm />
 * </PermissionGuard>
 * ```
 *
 * @example Multiple permissions with AND logic
 * ```tsx
 * // Show workflow panel only if user can both read AND execute
 * <PermissionGuard
 *   apiMethod={[
 *     API_METHODS.CaseRecordsService.GetCaseRecord,
 *     API_METHODS.CaseRecordsService.ExecuteAction,
 *   ]}
 *   requirementType="all"
 * >
 *   <WorkflowExecutionPanel />
 * </PermissionGuard>
 * ```
 *
 * @example Guard entire sections
 * ```tsx
 * <PermissionGuard apiMethod={API_METHODS.CampaignService.CreateCampaign}>
 *   <CreateCampaignForm />
 * </PermissionGuard>
 * ```
 */
export function PermissionGuard({
  apiMethod,
  children,
  fallback = null,
  requirementType = 'all',
}: PermissionGuardProps) {
  // Use the hook that now supports multiple methods natively
  const { hasPermission } = useAPIPermission(apiMethod, requirementType);

  // If user has permission, render children
  if (hasPermission) {
    return <>{children}</>;
  }

  // If no permission and no fallback, render nothing
  if (!fallback) {
    return null;
  }

  // Otherwise just render fallback
  return <>{fallback}</>;
}
